import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MANIFESTO } from "@/constants/testIds";
import { SITE } from "@/constants/site";

const chapters = [
  {
    n: "I.",
    title: "The work is quiet.",
    body: "Nobody sees the 5 a.m. sled push. Nobody sees the film session at midnight. The work is quiet — until it isn't. Then it's everything.",
  },
  {
    n: "II.",
    title: "Talent is a rumor.",
    body: "Talent gets you noticed. Habits get you paid. TWGAS is engineered around the habits behind the highlights.",
  },
  {
    n: "III.",
    title: "The body listens.",
    body: "Position-specific training, strength, and recovery — the body is a system and we tune it like one.",
  },
  {
    n: "IV.",
    title: "The mind decides.",
    body: "Film. Financial literacy. Leadership. If the mind isn't developed, the body is a wasted asset.",
  },
];

const Manifesto = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const bigTextX = useTransform(scrollYProgress, [0, 1], ["10%", "-20%"]);

  return (
    <section
      ref={ref}
      data-testid={MANIFESTO.root}
      className="section-rule bg-[#0A0A0A] py-28 md:py-36 relative overflow-hidden"
    >
      {/* Giant parallax word */}
      <motion.div
        style={{ x: bigTextX }}
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-6 md:-bottom-16 left-0 right-0 whitespace-nowrap font-display uppercase text-[28vw] leading-none stroke-text opacity-30 select-none"
      >
        TWGAS · TWGAS · TWGAS · TWGAS
      </motion.div>

      <div className="relative mx-auto max-w-[1440px] px-5 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-6 mb-12 md:mb-16">
          <div className="md:col-span-5">
            <div className="num-tag mb-4">/ CHAPTER 04 · MANIFESTO</div>
            <motion.h2
              initial={{ y: 40, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="font-display uppercase text-5xl md:text-7xl leading-[0.9] tracking-tight"
            >
              Better athletes.
              <br />
              Better people.
              <br />
              <span className="stroke-fire">Bigger opportunities.</span>
            </motion.h2>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <img
              src={SITE.logo}
              alt="TWGAS crest"
              className="w-40 h-40 md:w-56 md:h-56 mb-6 md:ml-auto md:mr-0"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          {chapters.map((c, i) => (
            <motion.div
              key={c.n}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{
                duration: 0.7,
                delay: 0.08 * i,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="md:col-span-6 border-t border-white/10 pt-6"
            >
              <div className="flex items-baseline gap-5">
                <span className="font-display text-[#FF4D00] text-4xl leading-none">
                  {c.n}
                </span>
                <h3 className="font-display uppercase text-3xl md:text-4xl leading-none tracking-tight">
                  {c.title}
                </h3>
              </div>
              <p className="mt-4 text-zinc-400 text-base md:text-lg leading-relaxed max-w-[54ch]">
                {c.body}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Manifesto;
